/**
 * Where you stand (SYR-111 v3, Group A).
 *
 * The fact statements: price, discounting, availability, advertising, the
 * website, the staff, the recommend question. These are the only statements
 * where the three models agree with each other closely enough for a
 * comparison between brands to mean anything (+0.87 to +1.00 on nine of
 * twelve), so this is the only place the page puts competitors in columns.
 *
 * Cells are the panel consensus after searching, in the models' own view.
 * The brand's cell also carries how far searching moved it from memory,
 * because that movement is the record problem and nothing else isolates it.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { Space, Table, Tag, Tooltip, Typography } from 'antd';
import { Card } from '../../atoms';
import { ReadSection } from './ReadSection';
import { InfoTip } from './InfoTip';
import { THEME } from '../../config/theme';
import { getDisplayName } from '../../../../utils/elementDisplayNames';
import { IModelBeliefsPageReading, IModelBeliefsStatementRead } from '../../../../services/nestjs-base-api';
import { StatementDetail } from './StatementDetail';
import {
  anchorFor,
  deltaColour,
  deltaInBrandTerms,
  rawDelta,
  fmt,
  readCell,
  readPages,
  reasonLabel,
  signed,
} from './beliefCells';

const { Text, Paragraph } = Typography;

interface Props {
  brandName: string;
  statements: IModelBeliefsStatementRead[];
  panelCoverage?: { brand?: string[]; competitor?: string[]; asymmetric?: boolean } | null;
  // The synthesis's paragraph on this table, when a synthesis has run.
  note?: string | null;
  // v4: the searched view is what the pages say. Brands are then compared
  // from memory, because competitors are not page-read, and the pages get a
  // column of their own beside the brand.
  pagesMode?: boolean;
  pagesRead?: number;
  // v4: every found page reading, keyed by statement code.
  pageReadings?: Record<string, IModelBeliefsPageReading[]>;
  n?: string;
}

interface Row {
  key: string;
  code: string;
  reasons: { label: string; text: string }[];
  readings: IModelBeliefsPageReading[];
  text: string;
  category: string;
  scaleMax: number;
  higherIsBetter: boolean;
  selfFromRank: boolean;
  brand: ReturnType<typeof readCell>;
  memory: ReturnType<typeof readCell>;
  pages: ReturnType<typeof readPages>;
  competitors: Record<string, ReturnType<typeof readCell>>;
  // Whether the models agree with each other on the brand's score, and the
  // best brand on the line. Both decided by the API with the written read's
  // rules, so the page never underlines a brand the read calls a tie.
  agree: 'yes' | 'mostly' | 'no' | 'unknown';
  best: string | null;
}

export const FactGrid: React.FunctionComponent<Props> = ({
  brandName,
  statements,
  panelCoverage,
  note,
  pagesMode = false,
  pagesRead = 0,
  pageReadings,
  n = '2',
}) => {
  const intl = useIntl();
  const facts = statements.filter((s) => s.group === 'A');
  if (facts.length === 0) return null;

  const competitorNames = facts[0]?.competitors?.map((c) => c.name) ?? [];

  const rows: Row[] = facts.map((s) => {
    const brand = readCell(pagesMode ? s.brand.memory : s.brand.searched, 'self');
    const memory = readCell(s.brand.memory, 'self');
    const pages = readPages(s.brand.pages);
    const competitors = Object.fromEntries(
      s.competitors.map((c) => [c.name, readCell(pagesMode ? c.memory : c.searched, 'self')]),
    );
    return {
      key: s.code,
      code: s.code,
      reasons: s.reasons ?? [],
      readings: pageReadings?.[s.code] ?? [],
      text: s.text,
      category: s.category,
      scaleMax: s.scaleMax,
      higherIsBetter: s.higherIsBetter,
      selfFromRank: s.selfFromRank,
      brand,
      memory,
      pages,
      competitors,
      agree: s.agree ?? 'unknown',
      best: s.best ?? null,
    };
  });

  const cell = (
    reading: ReturnType<typeof readCell>,
    row: Row,
    name: string,
    emphasise: boolean,
  ) => {
    if (reading.value === null) {
      return (
        <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
          {reasonLabel(reading.reason)}
        </Text>
      );
    }
    const isBest = row.best === name;
    return (
      <Tooltip title={`${anchorFor(reading.value, row.scaleMax)} · ${reading.n} answers`}>
        <Text
          strong={emphasise || isBest}
          style={{
            color: isBest ? THEME.textBase : emphasise ? THEME.textBase : THEME.textSecondary,
            borderBottom: isBest ? `2px solid ${THEME.primary}` : undefined,
          }}
        >
          {fmt(reading.value)}
        </Text>
      </Tooltip>
    );
  };

  const pagesSub = intl.formatMessage(
    {
      id: 'marketerView.modelBeliefs.facts.pagesSub',
      defaultMessage:
        'Each model rated these statements for {brand} and for each competitor from memory, from 1 (strongly disagree) to 7 (strongly agree); the recommend question is 0 to 10. The number shown is the average across the models, and the underlined score is the best brand on that line. These are the things a model can check, such as price, discounting, availability, advertising, the website and the staff, which is why they are the ones compared against competitors. The pages say is what the pages the models read say about {brand} on the same statement, scored page by page; the small green or red number is whether the pages say more or less for you than the models remember. Models agree? says whether the three models agree with each other on your score.',
    },
    { brand: brandName },
  );

  const sub = pagesMode ? pagesSub : (
    <>
      Each model rated these statements for {brandName} and for each competitor after searching the
      web, from 1 (strongly disagree) to 7 (strongly agree); the recommend question is 0 to 10. The
      number shown is the average across the models. These are the things a model can check, such as
      price, discounting, availability, advertising, the website and the staff, which is why they are
      the ones compared against competitors. The underlined score is the best brand on that line. The
      small green or red number beside your score is how far searching the web moved the models from
      what they already believed about you. Models agree? says whether the three models agree with
      each other on your score; where they do not, read the line with care.{' '}
      <InfoTip title="Each score's tooltip shows the scale wording it sits at (for example “agree”) and how many answers it averages.">
        Hover any score for its wording and how many answers are behind it.
      </InfoTip>
    </>
  );

  return (
    <ReadSection id="facts" n={n} title="Where do you stand on the facts?" sub={sub}>
      <Card>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {note && (
          <Paragraph style={{ marginBottom: 0, maxWidth: '72ch', fontSize: 14.5, lineHeight: 1.6 }}>{note}</Paragraph>
        )}

        {panelCoverage?.asymmetric && (
          <Text style={{ color: '#b45309', fontSize: 13 }}>
            Your brand was read by {(panelCoverage.brand ?? []).join(', ')} and the
            competitors by {(panelCoverage.competitor ?? []).join(', ')}, so a gap between you
            and a competitor is partly a difference of instrument.
          </Text>
        )}

        <Table
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          expandable={{
            rowExpandable: (row: Row) => row.reasons.length > 0 || row.readings.length > 0 || pagesMode,
            expandedRowRender: (row: Row) => (
              <StatementDetail reasons={row.reasons} readings={row.readings} pagesMode={pagesMode} />
            ),
          }}
          dataSource={rows}
          columns={[
            {
              title: 'Statement',
              dataIndex: 'text',
              width: 260,
              render: (text: string, row: Row) => (
                <Space direction="vertical" size={0}>
                  <Text style={{ fontSize: 13 }}>{text}</Text>
                  <Space size={4} wrap>
                    <Text style={{ color: THEME.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>
                      {getDisplayName(row.category)}
                    </Text>
                    {!row.higherIsBetter && (
                      <Tag style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                        <InfoTip title="On this statement a lower score is the better result for a brand.">
                          lower is better
                        </InfoTip>
                      </Tag>
                    )}
                    {row.selfFromRank && (
                      <Tag style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                        <InfoTip title="Scored from the recommendation question above: named first = 7, down to not in the six = 1. A statement that names you cannot measure whether you come to mind unprompted, so the models were not asked it directly.">
                          from the recommendation question
                        </InfoTip>
                      </Tag>
                    )}
                  </Space>
                </Space>
              ),
            },
            {
              title: pagesMode ? (
                <Space direction="vertical" size={0}>
                  <Text strong>{brandName}</Text>
                  <Text style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 400 }}>
                    {intl.formatMessage({
                      id: 'marketerView.modelBeliefs.facts.fromMemory',
                      defaultMessage: 'from memory',
                    })}
                  </Text>
                </Space>
              ) : (
                <Text strong>{brandName}</Text>
              ),
              key: 'brand',
              width: 150,
              render: (_: unknown, row: Row) => {
                const delta = deltaInBrandTerms(
                  row.brand.value,
                  row.memory.value,
                  row.higherIsBetter,
                );
                return (
                  <Space size={6}>
                    {cell(row.brand, row, brandName, true)}
                    {!pagesMode && delta !== null && Math.abs(delta) >= 0.3 && (
                      <Text style={{ color: deltaColour(delta), fontSize: 12 }}>
                        <InfoTip
                          title={`From memory ${fmt(row.memory.value)}, after searching ${fmt(
                            row.brand.value,
                          )}. ${delta < 0 ? 'What the models read on the web moved them against you.' : 'What the models read on the web moved them in your favour.'}`}
                        >
                          {signed(rawDelta(row.brand.value, row.memory.value))}
                        </InfoTip>
                      </Text>
                    )}
                  </Space>
                );
              },
            },
            ...(pagesMode
              ? [
                  {
                    title: (
                      <InfoTip
                        title={intl.formatMessage({
                          id: 'marketerView.modelBeliefs.pages.columnTip',
                          defaultMessage:
                            'What the pages the models read say about you on this statement, scored page by page from 1 to 7 and weighted by how often each page was read. Hover a score for how many pages speak to it.',
                        })}
                      >
                        {intl.formatMessage({
                          id: 'marketerView.modelBeliefs.pages.column',
                          defaultMessage: 'The pages say',
                        })}
                      </InfoTip>
                    ),
                    key: 'pages',
                    width: 140,
                    render: (_: unknown, row: Row) => {
                      if (row.pages.value === null) {
                        return (
                          <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
                            {intl.formatMessage({
                              id: 'marketerView.modelBeliefs.pages.nothing',
                              defaultMessage: 'Nothing',
                            })}
                          </Text>
                        );
                      }
                      const move = deltaInBrandTerms(row.pages.value, row.memory.value, row.higherIsBetter);
                      return (
                        <Space size={6}>
                          <Tooltip
                            title={intl.formatMessage(
                              {
                                id: 'marketerView.modelBeliefs.pages.cellTip',
                                defaultMessage: '{covering} of {read} pages read speak to this',
                              },
                              { covering: row.pages.pages, read: pagesRead },
                            )}
                          >
                            <Text strong>{fmt(row.pages.value)}</Text>
                          </Tooltip>
                          {move !== null && Math.abs(move) >= 0.3 && (
                            <Text style={{ color: deltaColour(move), fontSize: 12 }}>
                              {signed(rawDelta(row.pages.value, row.memory.value))}
                            </Text>
                          )}
                        </Space>
                      );
                    },
                  },
                ]
              : []),
            ...competitorNames.map((name) => ({
              title: name,
              key: name,
              width: 130,
              render: (_: unknown, row: Row) => cell(row.competitors[name], row, name, false),
            })),
            {
              title: (
                <InfoTip title="How far apart the models are on your score. Within one point they agree, within two mostly; wider than that, treat the line with care.">
                  Models agree?
                </InfoTip>
              ),
              key: 'agree',
              width: 120,
              render: (_: unknown, row: Row) =>
                row.agree === 'unknown' ? (
                  <Text style={{ color: THEME.textMuted }}>—</Text>
                ) : (
                  <Tag color={row.agree === 'yes' ? 'green' : row.agree === 'mostly' ? 'gold' : 'red'}>
                    {row.agree === 'yes' ? 'Yes' : row.agree === 'mostly' ? 'Mostly' : 'No'}
                  </Tag>
                ),
            },
          ]}
        />

        <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, fontSize: 13, maxWidth: '72ch' }}>
          {pagesMode
            ? intl.formatMessage({
                id: 'marketerView.modelBeliefs.facts.pagesFoot',
                defaultMessage:
                  'The small number beside the pages score is how far the pages sit from what the models remember, in your favour (green) or against you (red). Competitors are read at half your sample size; rank order is the reliable comparison, a gap of a few tenths is not.',
              })
            : 'The small number beside your score is how far searching moved the models from what they already believed, in your favour (green) or against you (red). Competitors are read at half your sample size; rank order is the reliable comparison, a gap of a few tenths is not.'}
        </Paragraph>
      </Space>
      </Card>
    </ReadSection>
  );
};
